export default {
  mounted() {
    this._queryElements();
    this.open = false;
    this._highlightedIndex = -1;

    this.toggle.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.open) {
        this._close();
      } else {
        this.input.focus();
        this._open();
      }
    });

    // Clicking anywhere in the control (but not a chip's remove button)
    // focuses the input and opens the dropdown.
    this.control.addEventListener("mousedown", (e) => {
      if (e.target.closest("[data-smsel-remove]")) return;
      if (e.target === this.input) return;
      e.preventDefault();
      this.input.focus();
      if (!this.open) this._open();
    });

    this.input.addEventListener("focus", () => {
      if (!this.open) this._open();
    });

    this.input.addEventListener("input", (e) => {
      e.stopPropagation();
      this._highlightedIndex = -1;
      this._filter();
      this._updateHighlight();
      this._pushSearch();
    });

    // Prevent bubbling to parent forms
    this.input.addEventListener("change", (e) => e.stopPropagation());

    this.input.addEventListener("keydown", (e) => {
      e.stopPropagation();
      const visible = this._visibleOptions();

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          this._close();
          break;
        case "Backspace":
          if (this.input.value === "") {
            const chips = this.el.querySelectorAll(
              "[data-smsel-chip][data-value]",
            );
            const last = chips[chips.length - 1];
            if (last) this._remove(last.dataset.value);
          }
          break;
        case "ArrowDown":
          e.preventDefault();
          if (!this.open) {
            this._open();
            return;
          }
          if (visible.length > 0) {
            this._highlightedIndex = Math.min(
              this._highlightedIndex + 1,
              visible.length - 1,
            );
            this._updateHighlight();
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          if (visible.length > 0) {
            this._highlightedIndex = Math.max(this._highlightedIndex - 1, 0);
            this._updateHighlight();
          }
          break;
        case "Enter":
          e.preventDefault();
          if (
            this._highlightedIndex >= 0 &&
            this._highlightedIndex < visible.length
          ) {
            this._toggle(visible[this._highlightedIndex].dataset.value);
          }
          break;
        case "Tab":
          if (this.open) this._close();
          break;
      }
    });

    // Delegate clicks on options and chip-remove buttons
    this.el.addEventListener("click", (e) => {
      const remove = e.target.closest("[data-smsel-remove]");
      if (remove && remove.dataset.value) {
        e.preventDefault();
        e.stopPropagation();
        this._remove(remove.dataset.value);
        this.input.focus();
        return;
      }

      const option = e.target.closest("[data-smsel-option]");
      if (option) {
        e.preventDefault();
        e.stopPropagation();
        this._toggle(option.dataset.value);
      }
    });

    document.addEventListener(
      "click",
      (this._clickAway = (e) => {
        if (this.open && !this.el.contains(e.target)) this._close();
      }),
    );
  },

  beforeUpdate() {
    this._wasOpen = this.open;
    this._searchText = this.input ? this.input.value : "";
    this._hadFocus = !!this.input && this.input === document.activeElement;
  },

  updated() {
    this._queryElements();
    this._syncChips();

    if (this._wasOpen) {
      this.open = true;
      this.dropdown.style.display = "";
      this.input.value = this._searchText;
      if (this.input) this.input.setAttribute("aria-expanded", "true");
      this._filter();
      this._updateHighlight();
      if (this._hadFocus) this.input.focus();
    } else {
      this.open = false;
      if (this.dropdown) this.dropdown.style.display = "none";
      if (this.input) {
        this.input.setAttribute("aria-expanded", "false");
        this.input.removeAttribute("aria-activedescendant");
      }
    }

    this._updatePlaceholder();
  },

  destroyed() {
    clearTimeout(this._searchTimer);
    document.removeEventListener("click", this._clickAway);
  },

  // With `data-search-event` set, the server owns the option list: send the
  // typed text (debounced) and let it patch the options in. The client-side
  // filter still runs on whatever is rendered, so both modes share the rest.
  _pushSearch() {
    const event = this.el.dataset.searchEvent;
    if (!event) return;
    clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => {
      this.pushEvent(event, { id: this.el.id, query: this.input.value });
    }, 200);
  },

  _queryElements() {
    this.control = this.el.querySelector("[data-smsel-control]");
    this.input = this.el.querySelector("[data-smsel-input]");
    this.toggle = this.el.querySelector("[data-smsel-toggle]");
    this.dropdown = this.el.querySelector("[data-smsel-dropdown]");
    this.fallbackInput = this.el.querySelector("[data-smsel-fallback]");
    this.chipTemplate = this.el.querySelector("[data-smsel-chip-template]");
    this._prompt = this.input ? this.input.dataset.prompt || "Search..." : "";
  },

  _open() {
    this.open = true;
    this._highlightedIndex = -1;
    this.dropdown.style.display = "";
    if (this.input) this.input.setAttribute("aria-expanded", "true");
    this._filter();
    this._updateHighlight();
  },

  _close() {
    this.open = false;
    this._highlightedIndex = -1;
    if (this.dropdown) this.dropdown.style.display = "none";
    if (this.input) {
      this.input.value = "";
      this.input.setAttribute("aria-expanded", "false");
      this.input.removeAttribute("aria-activedescendant");
    }
    this._filter();
  },

  _filter() {
    const term = this.input ? this.input.value.toLowerCase() : "";
    this.el.querySelectorAll("[data-smsel-option]").forEach((li) => {
      const label = li.dataset.label.toLowerCase();
      li.style.display = !term || label.includes(term) ? "" : "none";
    });
  },

  _visibleOptions() {
    return Array.from(this.el.querySelectorAll("[data-smsel-option]")).filter(
      (li) => li.style.display !== "none",
    );
  },

  // Keeps the highlighted option's styling and aria-activedescendant (on the
  // filter input) in sync - only set when something is actually highlighted,
  // since this hook has no keyboard navigation beyond what already exists
  // above (arrow keys moving `_highlightedIndex`).
  _updateHighlight() {
    const visible = this._visibleOptions();
    let highlighted = null;

    visible.forEach((li, i) => {
      if (i === this._highlightedIndex) {
        li.classList.add("bg-primary-600", "text-white");
        li.scrollIntoView({ block: "nearest" });
        highlighted = li;
      } else {
        li.classList.remove("bg-primary-600", "text-white");
      }
    });

    if (this.input) {
      if (highlighted && highlighted.id) {
        this.input.setAttribute("aria-activedescendant", highlighted.id);
      } else {
        this.input.removeAttribute("aria-activedescendant");
      }
    }
  },

  _toggle(value) {
    const isSelected = !!this.el.querySelector(
      `[data-smsel-hidden][data-value="${CSS.escape(value)}"]`,
    );

    if (isSelected) {
      this._deselect(value);
    } else {
      this._select(value);
    }

    this._updatePlaceholder();
    this._dispatchChange();
  },

  // Chip-remove and Backspace always deselect - never toggle. If the chip
  // and hidden-input sets ever drift, a toggle on a chip with no matching
  // hidden input would re-select the value instead of removing it.
  _remove(value) {
    this._deselect(value);
    this._updatePlaceholder();
    this._dispatchChange();
  },

  // The chips live inside phx-update="ignore" while the hidden inputs (the
  // source of truth) are server-patched; after every LiveView update,
  // rebuild the chip set - and each option's selected state - from the
  // hidden inputs so the two can never drift apart.
  _syncChips() {
    if (!this.control || !this.chipTemplate) return;

    const values = Array.from(
      this.el.querySelectorAll("[data-smsel-hidden]"),
    ).map((h) => h.dataset.value);
    const present = new Set(values);

    this.el
      .querySelectorAll("[data-smsel-control] [data-smsel-chip][data-value]")
      .forEach((chip) => {
        if (!present.has(chip.dataset.value)) chip.remove();
      });

    values.forEach((value) => {
      if (
        !this.el.querySelector(
          `[data-smsel-control] [data-smsel-chip][data-value="${CSS.escape(value)}"]`,
        )
      ) {
        this._addChip(value);
      }
    });

    this.el.querySelectorAll("[data-smsel-option]").forEach((option) => {
      const selected = present.has(option.dataset.value);
      option.setAttribute("aria-selected", selected ? "true" : "false");
      const check = option.querySelector("[data-smsel-check]");
      if (check) check.classList.toggle("hidden", !selected);
    });

    this._updatePlaceholder();
  },

  _addChip(value) {
    const option = this.el.querySelector(
      `[data-smsel-option][data-value="${CSS.escape(value)}"]`,
    );
    const label = option ? option.dataset.label : value;

    const chip = this.chipTemplate.content.firstElementChild.cloneNode(true);
    chip.id = `${this.el.id}-chip-${value}`;
    chip.dataset.value = value;
    chip.querySelector("[data-smsel-chip-label]").textContent = label;
    chip.querySelector("[data-smsel-remove]").dataset.value = value;
    this.control.insertBefore(chip, this.input);
  },

  _select(value) {
    const option = this.el.querySelector(
      `[data-smsel-option][data-value="${CSS.escape(value)}"]`,
    );
    if (option) option.setAttribute("aria-selected", "true");

    // Same ids the server renders for a selected value, so a LiveView patch
    // after save matches these nodes instead of morphing them into others.
    const hidden = document.createElement("input");
    hidden.id = `${this.el.id}-hidden-${value}`;
    hidden.type = "hidden";
    hidden.name = `${this.el.dataset.smselName}[]`;
    hidden.value = value;
    hidden.setAttribute("data-smsel-hidden", "");
    hidden.dataset.value = value;
    this.el.insertBefore(hidden, this.control);

    this._addChip(value);

    const check = option ? option.querySelector("[data-smsel-check]") : null;
    if (check) check.classList.remove("hidden");
  },

  _deselect(value) {
    const hidden = this.el.querySelector(
      `[data-smsel-hidden][data-value="${CSS.escape(value)}"]`,
    );
    if (hidden) hidden.remove();

    const chip = this.el.querySelector(
      `[data-smsel-control] [data-smsel-chip][data-value="${CSS.escape(value)}"]`,
    );
    if (chip) chip.remove();

    const option = this.el.querySelector(
      `[data-smsel-option][data-value="${CSS.escape(value)}"]`,
    );
    const check = option ? option.querySelector("[data-smsel-check]") : null;
    if (check) check.classList.add("hidden");
    if (option) option.setAttribute("aria-selected", "false");
  },

  _updatePlaceholder() {
    const hasSelection = !!this.el.querySelector(
      "[data-smsel-control] [data-smsel-chip][data-value]",
    );
    this.input.placeholder = hasSelection ? "" : this._prompt;
  },

  _dispatchChange() {
    const target = this.fallbackInput || this.input;
    target.dispatchEvent(new Event("input", { bubbles: true }));
  },
};
